import { z } from 'zod';
import { PAGINATION_PAGE_SIZE_DEFAULT } from '~/constants';

import {
  ACTIVATION_FEATURE_TYPE,
  ACTIVATION_REQUEST_STATE,
  GLOBAL_SUPPLIER_STATUS,
  RELATION_ACTIVATION_STATUS,
  SUPPLIER_ACTIVATION_STATUS,
} from '../constants';
import {
  zodPerChannelFlagsSchema,
  zodSupplierObjectSchema,
} from '../validations';

/**
 * Zod schema for a responsible-person reference returned in supplier overview items.
 */
const zodResponsiblePersonObjectSchema = z.object({
  id: z.uuid(),
  name: z.string(),
});

/**
 * Zod schema for the filter fields shared by the suppliers overview and
 * overview-counts query params — every filter except how strictly
 * `activationStatusDln`/`activationStatusInvoice` are typed (see each
 * endpoint's own schema).
 */
export const zodSuppliersOverviewFiltersBaseSchema = z.object({
  activationStatuses: z.array(z.enum(SUPPLIER_ACTIVATION_STATUS)).nullish(),
  contactAttemptFrom: z.string().nullish(),
  contactAttemptUntil: z.string().nullish(),
  globalStatus: z.array(z.enum(GLOBAL_SUPPLIER_STATUS)).nullish(),
  isHighPriority: z.boolean().nullish(),
  minPriorityScore: z.number().int().nullish(),
  recipientCompanyIds: z.preprocess(
    (value) => (value === undefined || value === null ? value : [value].flat()),
    z.array(z.uuid()).min(1),
  ),
  responsiblePersonId: z.array(z.uuid()).nullish(),
  searchString: z.string().trim().nullish(),
  supplierIds: z.array(z.uuid()).nullish(),
  supplierNumber: z.string().nullish(),
});

/**
 * Zod schema for the suppliers overview filters.
 */
export const zodSuppliersOverviewFiltersSchema =
  zodSuppliersOverviewFiltersBaseSchema.extend({
    activationStatusDln: z.array(z.enum(RELATION_ACTIVATION_STATUS)).nullish(),
    activationStatusInvoice: z
      .array(z.enum(RELATION_ACTIVATION_STATUS))
      .nullish(),
  });

/**
 * Zod schema for the suppliers overview query parameters.
 */
export const zodSuppliersOverviewQueryParamsSchema =
  zodSuppliersOverviewFiltersSchema.extend({
    limit: z
      .number()
      .int()
      .min(1)
      .max(500)
      .prefault(PAGINATION_PAGE_SIZE_DEFAULT),
    offset: z.number().int().min(0).prefault(0),
  });

/**
 * Zod schema for an activation request summary embedded in supplier overview items.
 */
export const zodActivationRequestSummarySchema = z.object({
  activationBatchId: z.uuid().nullable(),
  documentType: z.enum(ACTIVATION_FEATURE_TYPE),
  id: z.uuid(),
  state: z.enum(ACTIVATION_REQUEST_STATE),
});

/**
 * Zod schema for a single supplier card item in the overview list (SupplierCardOverviewItem).
 */
export const zodSupplierCardResponseSchema = z.object({
  activationRequests: z.array(zodActivationRequestSummarySchema).default([]),
  activationStatus: z.enum(SUPPLIER_ACTIVATION_STATUS),
  activationStatusDln: z.enum(RELATION_ACTIVATION_STATUS).nullish(),
  activationStatusInvoice: z.enum(RELATION_ACTIVATION_STATUS).nullish(),
  canPlanActivation: z.boolean(),
  canRequestActivation: zodPerChannelFlagsSchema,
  dataExchangeId: z.uuid(),
  globalStatus: z.enum(GLOBAL_SUPPLIER_STATUS).nullish(),
  hasSupplierNotes: z.boolean().default(false),
  isHighPriority: z.boolean(),
  responsiblePerson: zodResponsiblePersonObjectSchema.nullish(),
  supplier: zodSupplierObjectSchema,
});

/**
 * Zod schema for the paginated suppliers overview API response (SupplierOverviewResponse).
 */
export const zodSuppliersOverviewResponseSchema = z.object({
  data: z.array(zodSupplierCardResponseSchema),
  enabledModules: zodPerChannelFlagsSchema,
  hasNextPage: z.boolean(),
  letterAvailable: z.boolean().optional(),
  limit: z.number().int().nonnegative(),
  offset: z.number().int().nonnegative(),
  paginatedCount: z.number().int().nonnegative(),
  totalCount: z.number().int().nonnegative(),
});
